import { useCallback, useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { useAuth } from "../../../../providers/AuthProvider";

export type Brand = { id: string; name: string; status: string | null; product_count: number };

export function useBrandsManagement() {
  const { token } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!token) {
      setBrands([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.brands.list({ token });
      setBrands(res.data.map((b) => ({ id: String(b.id), name: b.name, status: b.status ?? null, product_count: Number(b.product_count ?? 0) })));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load brands");
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const createBrand = useCallback(
    async ({ name }: { name: string }) => {
      if (!token) throw new Error("Not authenticated");
      const res = await api.brands.create({ token, payload: { name: name.trim() } });
      setBrands((prev) => [...prev, { id: String(res.id), name: res.name, status: res.status ?? null, product_count: 0 }]);
    },
    [token],
  );

  const updateBrand = useCallback(
    async (id: string, payload: { name: string }) => {
      if (!token) throw new Error("Not authenticated");
      const res = await api.brands.update({ token, id, payload: { name: payload.name.trim() } });
      setBrands((prev) =>
        prev.map((b) =>
          b.id === String(id)
            ? { id: String(res.id), name: res.name, status: res.status ?? null, product_count: b.product_count }
            : b,
        ),
      );
    },
    [token],
  );

  const deleteBrand = useCallback(
    async (id: string) => {
      if (!token) throw new Error("Not authenticated");
      await api.brands.remove({ token, id });
      setBrands((prev) => prev.filter((b) => b.id !== String(id)));
    },
    [token],
  );

  return { brands, loading, error, reload, createBrand, updateBrand, deleteBrand };
}

